import type { LoaderFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { teamAPI, organizationAPI, APIError } from "../../lib/api";
import type {
  TeamResponse,
  TeamMemberResponse,
  MemberResponse,
  OrganizationPermissions,
} from "../../types/api";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

export interface TeamListData {
  teams: TeamResponse[];
  organizationId: string;
}

export interface TeamDetailData {
  team: TeamResponse;
  members: TeamMemberResponse[];
  organizationId: string;
}

export interface AddTeamMemberData {
  team: TeamResponse;
  currentMemberIds: string[];
  organizationId: string;
  organizationMembers: MemberResponse[];
}

export interface CreateTeamData {
  organizationId: string;
  teams: TeamResponse[];
  permissions: OrganizationPermissions;
}

export async function teamListLoader({ params }: LoaderFunctionArgs): Promise<TeamListData> {
  const { id: orgId } = params;

  if (!orgId || !isValidUUID(orgId)) {
    throw redirect("/organizations");
  }

  try {
    const teams = await teamAPI.getAll(orgId);
    return { teams, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}

export async function teamDetailLoader({ params }: LoaderFunctionArgs): Promise<TeamDetailData> {
  const { id: orgId, teamId } = params;

  if (!orgId || !teamId || !isValidUUID(orgId) || !isValidUUID(teamId)) {
    throw redirect("/organizations");
  }

  try {
    const [team, members] = await Promise.all([
      teamAPI.getById(orgId, teamId),
      teamAPI.getMembers(orgId, teamId),
    ]);

    return { team, members, organizationId: orgId };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/teams`);
    }
    throw error;
  }
}

export async function addTeamMemberLoader({
  params,
}: LoaderFunctionArgs): Promise<AddTeamMemberData> {
  const { id: orgId, teamId } = params;

  if (!orgId || !teamId || !isValidUUID(orgId) || !isValidUUID(teamId)) {
    throw redirect("/organizations");
  }

  try {
    const [team, teamMembers, organizationMembers] = await Promise.all([
      teamAPI.getById(orgId, teamId),
      teamAPI.getMembers(orgId, teamId),
      organizationAPI.getMembers(orgId),
    ]);

    const currentMemberIds = teamMembers.map((m) => m.userId);

    return { team, currentMemberIds, organizationId: orgId, organizationMembers };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect(`/organizations/${orgId}/teams`);
    }
    throw error;
  }
}

export async function createTeamLoader({ params }: LoaderFunctionArgs): Promise<CreateTeamData> {
  const { id: orgId } = params;

  if (!orgId || !isValidUUID(orgId)) {
    throw redirect("/organizations");
  }

  try {
    const [teams, orgResponse] = await Promise.all([
      teamAPI.getAll(orgId),
      organizationAPI.getById(orgId),
    ]);

    return {
      organizationId: orgId,
      teams,
      permissions: orgResponse.permissions,
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    throw error;
  }
}
